class PdpMediaEnhancements {
  constructor() {
    this.lightboxContainer = document.getElementById('product-zoom__container');
    this.lightboxDialog = this.lightboxContainer?.querySelector('.pdp-lightbox__dialog');
    this.lightboxMainWrapper = document.getElementById('pdp-lightbox-main-wrapper');
    this.lightboxThumbWrapper = document.getElementById('pdp-lightbox-thumb-wrapper');
    this.lightboxCloseButtons = this.lightboxContainer?.querySelectorAll('[data-pdp-lightbox-close]') || [];
    this.lightboxGestureHint = this.lightboxContainer?.querySelector('[data-pdp-lightbox-gesture-hint]');
    this.gestureHintTimerIds = [];
    this.gestureHintStorageKey = 'bargainmax:pdp-lightbox-gesture-hint:v1';
    this.gestureHintShownInSession = false;
    this.mainSwiper = null;
    this.thumbSwiper = null;
    this.thumbInteractionsBound = false;
    this.zoomStates = [];
    this.hoverOriginResetTimers = new WeakMap();
    this.lastFocusedElement = null;
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  init() {
    if (!this.lightboxContainer || !this.lightboxMainWrapper || !this.lightboxThumbWrapper) return;
    this.decorateTriggers();
    this.bindLightboxControls();
    this.bindLightboxThumbInteractions();
    this.bindHoverZoom();
    this.initializeBlurUp(document);
    this.preloadFirstGalleryImage();
  }

  decorateTriggers() {
    const triggerCandidates = document.querySelectorAll('.product-media__gallery-image, .product-media__grid-image');

    triggerCandidates.forEach((candidate) => {
      const sourceImage = candidate.querySelector('.js-pdp-gallery-source-image');
      if (!sourceImage) return;

      candidate.classList.add('js-pdp-lightbox-trigger');
      candidate.setAttribute('tabindex', '0');
      candidate.setAttribute('role', 'button');
      candidate.setAttribute('aria-haspopup', 'dialog');
      candidate.setAttribute('aria-label', sourceImage.alt ? `Open image gallery: ${sourceImage.alt}` : 'Open image gallery');

      candidate.addEventListener('click', (event) => {
        if (event.defaultPrevented) return;
        if (event.target.closest('a, button, video, iframe')) return;
        this.openFromTrigger(candidate);
      });

      candidate.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.openFromTrigger(candidate);
      });
    });
  }

  bindHoverZoom() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (window.innerWidth >= 768) return;

    const desktopImageCards = document.querySelectorAll('.product-media__grid-image');

    desktopImageCards.forEach((card) => {
      const image = card.querySelector('.js-pdp-gallery-source-image');
      if (!image) return;

      card.classList.add('js-pdp-hover-zoom');
      image.classList.add('js-pdp-hover-zoom-image');

      card.addEventListener('mouseenter', () => {
        this.clearHoverOriginResetTimer(card);
        card.classList.add('is-hovering');
      });

      card.addEventListener('mousemove', (event) => {
        const rect = card.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        const originX = ((event.clientX - rect.left) / rect.width) * 100;
        const originY = ((event.clientY - rect.top) / rect.height) * 100;
        image.style.transformOrigin = `${originX}% ${originY}%`;
      });

      card.addEventListener('mouseleave', () => {
        card.classList.remove('is-hovering');

        const resetDelayMs = this.getTransitionDurationMs(image) + 16;
        const timerId = window.setTimeout(() => {
          this.hoverOriginResetTimers.delete(card);
          if (card.classList.contains('is-hovering')) return;
          image.style.transformOrigin = '50% 50%';
        }, resetDelayMs);

        this.hoverOriginResetTimers.set(card, timerId);
      });
    });
  }

  clearHoverOriginResetTimer(card) {
    const existingTimer = this.hoverOriginResetTimers.get(card);
    if (!existingTimer) return;

    window.clearTimeout(existingTimer);
    this.hoverOriginResetTimers.delete(card);
  }

  parseDurationToMs(durationValue) {
    const normalized = String(durationValue || '').trim();
    if (!normalized) return 0;

    if (normalized.endsWith('ms')) {
      return Number.parseFloat(normalized);
    }

    if (normalized.endsWith('s')) {
      return Number.parseFloat(normalized) * 1000;
    }

    return Number.parseFloat(normalized) || 0;
  }

  getTransitionDurationMs(element) {
    if (!element) return 0;

    const computedStyles = window.getComputedStyle(element);
    const durations = computedStyles.transitionDuration.split(',').map((value) => this.parseDurationToMs(value));
    const delays = computedStyles.transitionDelay.split(',').map((value) => this.parseDurationToMs(value));
    const transitionsCount = Math.max(durations.length, delays.length);

    let maxDuration = 0;
    for (let index = 0; index < transitionsCount; index += 1) {
      const duration = durations[index] ?? durations[durations.length - 1] ?? 0;
      const delay = delays[index] ?? delays[delays.length - 1] ?? 0;
      maxDuration = Math.max(maxDuration, duration + delay);
    }

    return maxDuration;
  }

  bindLightboxControls() {
    this.lightboxCloseButtons.forEach((button) => {
      button.addEventListener('click', () => this.closeLightbox());
    });

    this.lightboxDialog?.addEventListener('click', (event) => {
      if (event.target === this.lightboxDialog) this.closeLightbox();
    });

    this.lightboxContainer.addEventListener('click', (event) => {
      if (event.target === this.lightboxContainer) this.closeLightbox();
    });
  }

  bindLightboxThumbInteractions() {
    if (this.thumbInteractionsBound || !this.lightboxThumbWrapper) return;

    const revealThumb = (button) => {
      if (!button || !this.thumbSwiper) return;

      const indexValue = Number(button.dataset.pdpThumbIndex);
      if (!Number.isFinite(indexValue) || indexValue < 0) return;

      this.thumbSwiper.slideTo(indexValue, 0, false);
    };

    const activateThumb = (button) => {
      if (!button || !this.mainSwiper) return;

      const indexValue = Number(button.dataset.pdpThumbIndex);
      if (!Number.isFinite(indexValue) || indexValue < 0) return;

      this.mainSwiper.slideTo(indexValue);
      revealThumb(button);
    };

    this.lightboxThumbWrapper.addEventListener('click', (event) => {
      const button = event.target.closest('.pdp-lightbox__thumb-button');
      if (!button) return;
      activateThumb(button);
    });

    this.lightboxThumbWrapper.addEventListener('keydown', (event) => {
      const button = event.target.closest('.pdp-lightbox__thumb-button');
      if (!button) return;

      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        activateThumb(button);
        return;
      }

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown' || event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        const buttons = Array.from(this.lightboxThumbWrapper.querySelectorAll('.pdp-lightbox__thumb-button'));
        const currentIndex = buttons.indexOf(button);
        if (currentIndex < 0) return;

        const direction = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
        const nextButton = buttons[currentIndex + direction];
        if (!nextButton) return;

        event.preventDefault();
        nextButton.focus();
        revealThumb(nextButton);
      }
    });

    this.lightboxThumbWrapper.addEventListener('focusin', (event) => {
      const button = event.target.closest('.pdp-lightbox__thumb-button');
      if (!button) return;
      revealThumb(button);
    });

    this.thumbInteractionsBound = true;
  }

  openFromTrigger(trigger) {
    const sourceImages = this.getActiveGalleryImages();
    if (!sourceImages.length) return;

    const clickedImage = trigger.querySelector('.js-pdp-gallery-source-image');
    const startIndex = Math.max(0, sourceImages.findIndex((image) => image === clickedImage));

    this.buildLightboxSlides(sourceImages);
    this.initializeSwipers(startIndex);
    this.initializeZoom();
    this.initializeBlurUp(this.lightboxContainer);

    this.lastFocusedElement = document.activeElement;
    this.lightboxContainer.classList.remove('hidden');
    this.lightboxContainer.classList.add('is-open');
    this.lightboxContainer.setAttribute('aria-hidden', 'false');

    document.documentElement.classList.add('pdp-lightbox-open');
    document.body.classList.add('pdp-lightbox-open');

    document.addEventListener('keydown', this.handleKeydown);

    this.hideGestureHint();
    this.maybeShowGestureHint();

    const closeButton = this.lightboxContainer.querySelector('#product-zoom__container-close');
    closeButton?.focus();
  }

  closeLightbox() {
    if (!this.lightboxContainer.classList.contains('is-open')) return;

    this.resetAllZoom(false);
    this.destroySwipers();
    this.hideGestureHint();

    this.lightboxContainer.classList.add('hidden');
    this.lightboxContainer.classList.remove('is-open');
    this.lightboxContainer.setAttribute('aria-hidden', 'true');

    document.documentElement.classList.remove('pdp-lightbox-open');
    document.body.classList.remove('pdp-lightbox-open');

    document.removeEventListener('keydown', this.handleKeydown);

    if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
      this.lastFocusedElement.focus();
    }

    this.lightboxMainWrapper.innerHTML = '';
    this.lightboxThumbWrapper.innerHTML = '';
  }

  maybeShowGestureHint() {
    if (!this.lightboxGestureHint || !window.matchMedia('(max-width: 989px)').matches) return;
    if (this.hasSeenGestureHint()) return;

    this.setGestureHintSeen();
    this.showGestureHint();
  }

  hasSeenGestureHint() {
    if (this.gestureHintShownInSession) return true;

    try {
      return window.localStorage.getItem(this.gestureHintStorageKey) === '1';
    } catch (error) {
      return false;
    }
  }

  setGestureHintSeen() {
    this.gestureHintShownInSession = true;

    try {
      window.localStorage.setItem(this.gestureHintStorageKey, '1');
    } catch (error) {
      // Ignore storage failures in private browsing or blocked-storage contexts.
    }
  }

  showGestureHint() {
    if (!this.lightboxGestureHint) return;

    this.clearGestureHintTimers();
    this.lightboxGestureHint.classList.remove('is-active');
    this.lightboxGestureHint.setAttribute('aria-hidden', 'true');

    const showTimerId = window.setTimeout(() => {
      this.lightboxGestureHint.classList.add('is-active');
      this.lightboxGestureHint.setAttribute('aria-hidden', 'false');
    }, 120);

    const hideTimerId = window.setTimeout(() => {
      this.hideGestureHint();
    }, 2600);

    this.gestureHintTimerIds.push(showTimerId, hideTimerId);
  }

  hideGestureHint() {
    this.clearGestureHintTimers();
    if (!this.lightboxGestureHint) return;

    this.lightboxGestureHint.classList.remove('is-active');
    this.lightboxGestureHint.setAttribute('aria-hidden', 'true');
  }

  clearGestureHintTimers() {
    if (!this.gestureHintTimerIds.length) return;

    this.gestureHintTimerIds.forEach((timerId) => window.clearTimeout(timerId));
    this.gestureHintTimerIds = [];
  }

  getActiveGalleryImages() {
    const desktopVisible = window.matchMedia('(min-width: 990px)').matches;
    const sourceScope = desktopVisible
      ? document.querySelector('.product-media__grid')
      : document.querySelector('.product-media__gallery');

    if (!sourceScope) return [];

    return Array.from(sourceScope.querySelectorAll('.js-pdp-gallery-source-image'));
  }

  buildLightboxSlides(sourceImages) {
    const mainSlides = [];
    const thumbSlides = [];

    sourceImages.forEach((sourceImage, index) => {
      const alt = this.escapeHtml(sourceImage.alt || `Product image ${index + 1}`);
      const highSrc = this.buildImageUrl(sourceImage.currentSrc || sourceImage.src, 2400);
      const thumbSrc = this.buildImageUrl(sourceImage.currentSrc || sourceImage.src, 180);
      const previewSrc = this.buildImageUrl(sourceImage.currentSrc || sourceImage.src, 56);
      const loadingValue = index === 0 ? 'eager' : 'lazy';

      mainSlides.push(`
        <div class="swiper-slide pdp-lightbox__slide" data-pdp-slide-index="${index}">
          <div class="pdp-lightbox__zoom-surface js-pdp-lightbox-zoom-surface">
            <span class="pdp-blur-up js-pdp-blur-up">
              <img src="${previewSrc}" alt="" aria-hidden="true" loading="eager" class="pdp-lightbox__image pdp-blur-up__placeholder">
              <img src="${highSrc}" alt="${alt}" loading="${loadingValue}" class="pdp-lightbox__image pdp-blur-up__target js-pdp-blur-target js-pdp-lightbox-zoom-target">
            </span>
          </div>
        </div>
      `);

      thumbSlides.push(`
        <div class="swiper-slide pdp-lightbox__thumb-slide">
          <button class="pdp-lightbox__thumb-button" type="button" data-pdp-thumb-index="${index}" aria-label="View image ${index + 1}">
            <img src="${thumbSrc}" alt="${alt}" loading="lazy" class="pdp-lightbox__thumb-image">
          </button>
        </div>
      `);
    });

    this.lightboxMainWrapper.innerHTML = mainSlides.join('');
    this.lightboxThumbWrapper.innerHTML = thumbSlides.join('');
  }

  initializeSwipers(startIndex) {
    this.destroySwipers();

    this.thumbSwiper = new Swiper('.pdp-lightbox__thumbs-swiper', {
      slidesPerView: 'auto',
      spaceBetween: 8,
      freeMode: true,
      watchSlidesProgress: true,
      threshold: 8,
    });

    this.mainSwiper = new Swiper('.pdp-lightbox__main-swiper', {
      slidesPerView: 1,
      spaceBetween: 12,
      threshold: 8,
      keyboard: {
        enabled: true,
      },
      thumbs: {
        swiper: this.thumbSwiper,
      },
      on: {
        slideChange: () => {
          this.resetAllZoom(true, this.mainSwiper.realIndex);
        },
      },
    });

    this.mainSwiper.slideTo(startIndex, 0, false);
    this.thumbSwiper.slideTo(startIndex, 0, false);
  }

  destroySwipers() {
    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
      this.mainSwiper = null;
    }

    if (this.thumbSwiper) {
      this.thumbSwiper.destroy(true, true);
      this.thumbSwiper = null;
    }

    this.zoomStates = [];
  }

  initializeZoom() {
    this.zoomStates = [];

    const surfaces = this.lightboxContainer.querySelectorAll('.js-pdp-lightbox-zoom-surface');

    surfaces.forEach((surface) => {
      const image = surface.querySelector('.js-pdp-lightbox-zoom-target');
      if (!image) return;

      const state = {
        surface,
        image,
        scale: 1,
        x: 0,
        y: 0,
        dragging: false,
        dragStartX: 0,
        dragStartY: 0,
        pointers: new Map(),
        pinchDistance: 0,
        pinchScale: 1,
        moved: false,
        lastTapTime: 0,
        lastTapX: 0,
        lastTapY: 0,
      };

      const updateSwipeState = () => {
        const zoomActive = state.scale > 1.01;
        surface.classList.toggle('is-zoomed', zoomActive);
        if (this.mainSwiper) {
          this.mainSwiper.allowTouchMove = !zoomActive;
          this.mainSwiper.allowSlideNext = !zoomActive;
          this.mainSwiper.allowSlidePrev = !zoomActive;
        }
      };

      const applyTransform = (animate = false) => {
        if (animate) {
          image.style.transition = 'transform 220ms ease';
        } else {
          image.style.transition = 'none';
        }

        image.style.transform = `translate3d(${state.x}px, ${state.y}px, 0) scale(${state.scale})`;

        if (animate) {
          window.setTimeout(() => {
            image.style.transition = 'none';
          }, 240);
        }

        updateSwipeState();
      };

      const clampPosition = () => {
        const containerWidth = surface.clientWidth;
        const containerHeight = surface.clientHeight;
        const maxX = Math.max(0, ((image.offsetWidth * state.scale) - containerWidth) / 2);
        const maxY = Math.max(0, ((image.offsetHeight * state.scale) - containerHeight) / 2);

        state.x = Math.min(maxX, Math.max(-maxX, state.x));
        state.y = Math.min(maxY, Math.max(-maxY, state.y));
      };

      const reset = (animate = true) => {
        state.scale = 1;
        state.x = 0;
        state.y = 0;
        applyTransform(animate);
      };

      const zoomTo = (nextScale, clientX, clientY, animate = false) => {
        const clampedScale = Math.min(2, Math.max(1, nextScale));
        if (Math.abs(clampedScale - state.scale) < 0.001) return;

        const rect = surface.getBoundingClientRect();
        const offsetX = clientX - (rect.left + rect.width / 2);
        const offsetY = clientY - (rect.top + rect.height / 2);
        const scaleRatio = clampedScale / state.scale;

        state.x = (state.x - offsetX) * scaleRatio + offsetX;
        state.y = (state.y - offsetY) * scaleRatio + offsetY;
        state.scale = clampedScale;

        clampPosition();
        applyTransform(animate);
      };

      const getPointerDistance = () => {
        const points = Array.from(state.pointers.values());
        if (points.length < 2) return 0;
        return Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
      };

      const getPointerCenter = () => {
        const points = Array.from(state.pointers.values());
        if (points.length < 2) return null;
        return {
          x: (points[0].x + points[1].x) / 2,
          y: (points[0].y + points[1].y) / 2,
        };
      };

      const toggleDoubleTapZoom = (clientX, clientY) => {
        if (state.scale > 1.01) {
          reset(true);
          return;
        }

        zoomTo(2, clientX, clientY, true);
      };

      surface.addEventListener('wheel', (event) => {
        event.preventDefault();
        const zoomDirection = event.deltaY < 0 ? 0.14 : -0.14;
        zoomTo(state.scale + zoomDirection, event.clientX, event.clientY);
      }, { passive: false });

      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        surface.addEventListener('dblclick', (event) => {
          event.preventDefault();
          toggleDoubleTapZoom(event.clientX, event.clientY);
        });
      }

      surface.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;

        if (state.scale > 1.01) {
          event.preventDefault();
          event.stopPropagation();
        }

        state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        state.moved = false;

        if (state.pointers.size === 2) {
          state.pinchDistance = getPointerDistance();
          state.pinchScale = state.scale;
        } else if (state.scale > 1.01) {
          state.dragging = true;
          state.dragStartX = event.clientX - state.x;
          state.dragStartY = event.clientY - state.y;
        }

        surface.setPointerCapture(event.pointerId);
      });

      surface.addEventListener('pointermove', (event) => {
        if (!state.pointers.has(event.pointerId)) return;

        const previousPoint = state.pointers.get(event.pointerId);
        state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (previousPoint && (Math.abs(previousPoint.x - event.clientX) > 1 || Math.abs(previousPoint.y - event.clientY) > 1)) {
          state.moved = true;
        }

        if (state.pointers.size === 2) {
          event.preventDefault();
          const currentDistance = getPointerDistance();
          if (!state.pinchDistance) {
            state.pinchDistance = currentDistance;
          }

          const pinchCenter = getPointerCenter();
          if (!pinchCenter || !state.pinchDistance) return;

          const pinchScale = state.pinchScale * (currentDistance / state.pinchDistance);
          zoomTo(pinchScale, pinchCenter.x, pinchCenter.y);
          return;
        }

        if (!state.dragging || state.scale <= 1.01) return;

        event.preventDefault();
        event.stopPropagation();
        state.x = event.clientX - state.dragStartX;
        state.y = event.clientY - state.dragStartY;

        clampPosition();
        applyTransform(false);
      });

      const onPointerRelease = (event) => {
        if (!state.pointers.has(event.pointerId)) return;

        const wasPinching = state.pointers.size > 1;
        state.pointers.delete(event.pointerId);

        if (state.pointers.size < 2) {
          state.pinchDistance = 0;
          state.pinchScale = state.scale;
        }

        if (!state.pointers.size) {
          state.dragging = false;
          clampPosition();
          applyTransform(false);

          if (state.scale <= 1.01) {
            reset(true);
          }

          const now = Date.now();
          const tapDelta = now - state.lastTapTime;
          const tapDistance = Math.hypot(event.clientX - state.lastTapX, event.clientY - state.lastTapY);

          if (!state.moved && !wasPinching && (event.pointerType === 'touch' || event.pointerType === 'pen')) {
            if (tapDelta < 300 && tapDistance < 24) {
              toggleDoubleTapZoom(event.clientX, event.clientY);
              state.lastTapTime = 0;
              state.lastTapX = 0;
              state.lastTapY = 0;
            } else {
              state.lastTapTime = now;
              state.lastTapX = event.clientX;
              state.lastTapY = event.clientY;
            }
          }
        }
      };

      surface.addEventListener('pointerup', onPointerRelease);
      surface.addEventListener('pointercancel', onPointerRelease);
      surface.addEventListener('pointerleave', onPointerRelease);

      state.reset = reset;
      this.zoomStates.push(state);
    });
  }

  resetAllZoom(skipAnimation = true, keepIndex = -1) {
    this.zoomStates.forEach((state, index) => {
      if (index === keepIndex) return;
      state.reset?.(!skipAnimation);
    });

    if (this.mainSwiper && keepIndex < 0) {
      this.mainSwiper.allowTouchMove = true;
      this.mainSwiper.allowSlideNext = true;
      this.mainSwiper.allowSlidePrev = true;
    }
  }

  preloadFirstGalleryImage() {
    const firstImage = document.querySelector('.product-media__gallery .js-pdp-gallery-source-image, .product-media__grid .js-pdp-gallery-source-image');
    if (!firstImage) return;

    const preloadHref = this.buildImageUrl(firstImage.currentSrc || firstImage.src, 2000);
    if (!preloadHref) return;

    if (document.querySelector(`link[rel="preload"][href="${preloadHref}"]`)) return;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = preloadHref;
    document.head.appendChild(link);
  }

  initializeBlurUp(scope) {
    const wrappers = scope.querySelectorAll('.js-pdp-blur-up');

    wrappers.forEach((wrapper) => {
      const target = wrapper.querySelector('.js-pdp-blur-target');
      if (!target) return;

      const markLoaded = () => wrapper.classList.add('is-loaded');

      if (target.complete) {
        markLoaded();
      } else {
        target.addEventListener('load', markLoaded, { once: true });
      }
    });
  }

  buildImageUrl(sourceUrl, width) {
    if (!sourceUrl) return '';

    try {
      const parsed = new URL(sourceUrl, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (error) {
      return sourceUrl;
    }
  }

  handleKeydown(event) {
    if (!this.lightboxContainer.classList.contains('is-open')) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeLightbox();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = this.lightboxDialog.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (!focusableElements.length) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

const initializePdpMediaEnhancements = () => {
  const enhancements = new PdpMediaEnhancements();
  enhancements.init();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializePdpMediaEnhancements);
} else {
  initializePdpMediaEnhancements();
}
